import { useEffect, useMemo } from "react";
import { ArrowLeft, LoaderCircle, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { BookAssemblyLaunchCard } from "@/components/book-assembly/book-assembly-launch-card";
import { BookAssemblyStepper } from "@/components/book-assembly/book-assembly-stepper";
import { BookExportGenerateStep } from "@/components/book-assembly/book-export-generate-step";
import { ChapterSelectionStep } from "@/components/book-assembly/chapter-selection-step";
import { ExportSettingsStep } from "@/components/book-assembly/export-settings-step";
import { SideStoryArrangementStep } from "@/components/book-assembly/side-story-placement-step";
import { PackagePreviewStep } from "@/components/book-assembly/toc-preview-step";
import { Button } from "@/components/ui/button";
import {
  createSignedBookExportArtifactDownload,
} from "@/features/book-assembly/api/bookAssemblyClient";
import type { BookAssemblyStep } from "@/features/book-assembly/book-assembly.types";
import {
  projectBookAssemblyStore,
  selectBookAssemblyCanGenerate,
  selectBookAssemblyChapterSelections,
  selectBookAssemblyPlacementChoices,
  useProjectBookAssemblyStore,
} from "@/stores/project-book-assembly-store";

function getPreviousStep(step: BookAssemblyStep): BookAssemblyStep | null {
  switch (step) {
    case "side-story-arrangement":
      return "chapter-selection";
    case "package-preview":
      return "side-story-arrangement";
    case "export-settings":
      return "package-preview";
    case "generate":
      return "export-settings";
    default:
      return null;
  }
}

function getNextStep(step: BookAssemblyStep): BookAssemblyStep | null {
  switch (step) {
    case "chapter-selection":
      return "side-story-arrangement";
    case "side-story-arrangement":
      return "package-preview";
    case "package-preview":
      return "export-settings";
    case "export-settings":
      return "generate";
    default:
      return null;
  }
}

export function ProjectBookAssemblyPage() {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const bookMetadataStatus = useProjectBookAssemblyStore((state) => state.bookMetadataStatus);
  const canGenerate = useProjectBookAssemblyStore(selectBookAssemblyCanGenerate);
  const dismissNotice = useProjectBookAssemblyStore((state) => state.dismissNotice);
  const draft = useProjectBookAssemblyStore((state) => state.draft);
  const generateBook = useProjectBookAssemblyStore((state) => state.generateBook);
  const jobs = useProjectBookAssemblyStore((state) => state.jobs);
  const moveTocItem = useProjectBookAssemblyStore((state) => state.moveTocItem);
  const notice = useProjectBookAssemblyStore((state) => state.notice);
  const patchExportConfig = useProjectBookAssemblyStore((state) => state.patchExportConfig);
  const patchSettings = useProjectBookAssemblyStore((state) => state.patchSettings);
  const prepareFreshExport = useProjectBookAssemblyStore((state) => state.prepareFreshExport);
  const reorderTocItems = useProjectBookAssemblyStore((state) => state.reorderTocItems);
  const requestSuggestionSet = useProjectBookAssemblyStore((state) => state.requestSuggestionSet);
  const applySuggestionSet = useProjectBookAssemblyStore((state) => state.applySuggestionSet);
  const retryJob = useProjectBookAssemblyStore((state) => state.retryJob);
  const saveBookMetadata = useProjectBookAssemblyStore((state) => state.saveBookMetadata);
  const setStep = useProjectBookAssemblyStore((state) => state.setStep);
  const showNotice = useProjectBookAssemblyStore((state) => state.showNotice);
  const source = useProjectBookAssemblyStore((state) => state.source);
  const status = useProjectBookAssemblyStore((state) => state.status);
  const suggestionStatus = useProjectBookAssemblyStore((state) => state.suggestionStatus);
  const toggleChapter = useProjectBookAssemblyStore((state) => state.toggleChapter);
  const activeJobId = useProjectBookAssemblyStore((state) => state.activeJobId);
  const isPreparingFreshExport = useProjectBookAssemblyStore((state) => state.isPreparingFreshExport);
  const updateSideStoryInclusion = useProjectBookAssemblyStore(
    (state) => state.updateSideStoryInclusion,
  );
  const updateSideStoryPlacement = useProjectBookAssemblyStore(
    (state) => state.updateSideStoryPlacement,
  );

  useEffect(() => {
    if (!projectId) {
      return;
    }

    void projectBookAssemblyStore.getState().hydrateWorkspace(projectId);
  }, [projectId]);

  const chapterSelections = useMemo(
    () => selectBookAssemblyChapterSelections({ draft, source }),
    [draft, source],
  );
  const placementChoices = useMemo(
    () => selectBookAssemblyPlacementChoices({ draft, source }),
    [draft, source],
  );

  const stepLabels = useMemo<Record<BookAssemblyStep, string>>(
    () => ({
      "chapter-selection": t("bookAssembly.stepLabels.chapterSelection"),
      "side-story-arrangement": t("bookAssembly.stepLabels.sideStories"),
      "package-preview": t("bookAssembly.stepLabels.readingOrder"),
      "export-settings": t("bookAssembly.stepLabels.exportSettings"),
      generate: t("bookAssembly.stepLabels.generate"),
    }),
    [t],
  );

  const chapterOptions = useMemo(
    () =>
      chapterSelections
        .filter((chapter) => chapter.included)
        .map((chapter) => ({
          id: chapter.chapterId,
          label: t("bookAssembly.common.chapterNumberTitle", {
            count: chapter.chapterNumber,
            title: chapter.title,
          }),
        })),
    [chapterSelections, t],
  );

  const summary = useMemo(() => ({
    bookTitle: draft?.settings.bookTitle ?? source?.exportIdentity?.title ?? source?.projectTitle ?? "",
    formatLabel: t(`bookAssembly.summary.targets.${draft?.summary?.target ?? draft?.exportConfig?.target ?? "pdf"}`),
    itemCount:
      draft?.summary?.sections
        ?.filter((section) => section.included)
        .reduce((total, section) => total + section.itemCount, 0)
      ?? draft?.orderedItems.length
      ?? 0,
  }), [
    draft?.exportConfig?.target,
    draft?.orderedItems.length,
    draft?.settings.bookTitle,
    draft?.summary?.sections,
    draft?.summary?.target,
    source?.exportIdentity?.title,
    source?.projectTitle,
    t,
  ]);
  const activeJob = useMemo(
    () => isPreparingFreshExport
      ? null
      : jobs.find((job) => job.id === activeJobId) ?? jobs[0] ?? null,
    [activeJobId, isPreparingFreshExport, jobs],
  );

  if (!projectId) {
    return (
      <section className="space-y-4 border-y border-ink/10 py-8">
          <h2 className="text-2xl text-foreground">{t("bookAssembly.errors.notFound")}</h2>
          <p className="text-muted-foreground">{t("projects.dashboard.missing")}</p>
      </section>
    );
  }

  if (status === "loading" || (status === "ready" && (!source || !draft))) {
    return (
      <section className="flex items-center gap-3 border-y border-ink/10 py-10 text-sm text-muted-foreground">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          {t("bookAssembly.loading")}
      </section>
    );
  }

  if (status === "error" || !source || !draft) {
    return (
      <section className="space-y-4 border-y border-ink/10 py-8">
          <h2 className="text-2xl text-foreground">{t("bookAssembly.errorTitle")}</h2>
          <p className="text-muted-foreground">
            {notice ? t(notice.message) : t("bookAssembly.errors.generationFailed")}
          </p>
          <div className="flex gap-3">
            <Button asChild type="button" variant="outline">
              <Link to={`/projects/${projectId}/stories`}>
                <ArrowLeft className="h-4 w-4" />
                {t("bookAssembly.actions.backToStories")}
              </Link>
            </Button>
            <Button
              onClick={() => void projectBookAssemblyStore.getState().hydrateWorkspace(projectId)}
              type="button"
            >
              {t("bookAssembly.actions.retry")}
            </Button>
          </div>
      </section>
    );
  }

  if (!source.availability.eligible) {
    return (
      <div className="space-y-6">
        <section className="space-y-4 border-y border-ink/10 py-8">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
              {t("bookAssembly.unavailable.eyebrow")}
            </p>
            <h1 className="font-display text-4xl text-ink">
              {t("bookAssembly.unavailable.title")}
            </h1>
            <p className="max-w-3xl text-base leading-7 text-muted-foreground">
              {source.availability.reason ? t(source.availability.reason) : t("bookAssembly.unavailable.body")}
            </p>
            <Button asChild type="button" variant="outline">
              <Link to={`/projects/${projectId}/stories`}>
                <ArrowLeft className="h-4 w-4" />
                {t("bookAssembly.actions.backToStories")}
              </Link>
            </Button>
        </section>
        <BookAssemblyLaunchCard projectId={projectId} />
      </div>
    );
  }

  const previousStep = getPreviousStep(draft.step);
  const nextStep = getNextStep(draft.step);

  return (
    <div className="space-y-6">
      <section className="border-b border-ink/10 pb-6">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.28em] text-clay">
              {t("bookAssembly.page.eyebrow")}
            </p>
            <div className="space-y-2">
              <h1 className="font-display text-5xl text-ink">{source.projectTitle}</h1>
              <p className="max-w-3xl text-base leading-7 text-ink/70">
                {t("bookAssembly.page.description")}
              </p>
            </div>
          </div>

          <Button asChild type="button" variant="outline">
            <Link to={`/projects/${projectId}/stories`}>
              <ArrowLeft className="h-4 w-4" />
              {t("bookAssembly.actions.backToStories")}
            </Link>
          </Button>
        </div>
      </section>

      {source.availability.tierLocked ? (
        <div className="border-y border-amber-600/20 py-4 text-sm text-amber-950">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-1">
              <p className="font-semibold">{t("bookAssembly.tierLock.title")}</p>
              <p>{t("bookAssembly.tierLock.body")}</p>
            </div>
            <Button asChild type="button">
              <Link to="/account/billing">
                <Sparkles className="h-4 w-4" />
                {t("bookAssembly.tierLock.cta")}
              </Link>
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <div
          className="border-y border-ink/10 py-4 text-sm text-foreground"
          role={notice.kind === "error" ? "alert" : "status"}
        >
          <div className="flex items-start justify-between gap-3">
            <p>{t(notice.message)}</p>
            <button
              className="text-muted-foreground transition-colors hover:text-foreground"
              onClick={dismissNotice}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      <BookAssemblyStepper
        currentStep={draft.step}
        labels={stepLabels}
        onStepSelect={setStep}
      />

      {draft.step === "chapter-selection" ? (
        <ChapterSelectionStep chapters={chapterSelections} onToggle={toggleChapter} />
      ) : null}

      {draft.step === "side-story-arrangement" ? (
        <SideStoryArrangementStep
          aiSuggestionsLocked={source.tier === "free"}
          chapterOptions={chapterOptions}
          choices={placementChoices}
          onApplySuggestionSet={applySuggestionSet}
          onIncludedChange={updateSideStoryInclusion}
          onPlacementChange={updateSideStoryPlacement}
          onRequestSuggestionSet={() => void requestSuggestionSet()}
          suggestionSet={draft.suggestionSet}
          suggestionStatus={suggestionStatus}
        />
      ) : null}

      {draft.step === "package-preview" ? (
        <PackagePreviewStep
          items={draft.orderedItems}
          onMove={moveTocItem}
          onReorder={reorderTocItems}
        />
      ) : null}

      {draft.step === "export-settings" ? (
        <ExportSettingsStep
          capabilities={source.capabilities}
          config={draft.exportConfig ?? source.capabilities.defaultConfig}
          defaultAuthorName={source.exportIdentity?.author_name ?? ""}
          defaultBookTitle={source.projectTitle}
          metadataStatus={bookMetadataStatus}
          onConfigChange={patchExportConfig}
          onSaveBookMetadata={() => void saveBookMetadata()}
          onSettingsChange={patchSettings}
          settings={draft.settings}
          summary={draft.summary}
        />
      ) : null}

      {draft.step === "generate" ? (
        <BookExportGenerateStep
          canGenerate={canGenerate && source.availability.tierLocked !== true}
          job={activeJob}
          onCopyArtifactLink={(target) => {
            const artifact = activeJob?.artifacts.find((candidate) => candidate.target === target);
            if (!artifact) {
              return;
            }

            void createSignedBookExportArtifactDownload(artifact)
              .then((signedArtifact) => navigator.clipboard.writeText(signedArtifact.downloadUrl))
              .catch(() => {
                showNotice({
                  kind: "error",
                  message: "bookAssembly.errors.downloadExpired",
                });
              });
          }}
          onDownloadArtifact={(target) => {
            const artifact = activeJob?.artifacts.find((candidate) => candidate.target === target);
            if (!artifact) {
              return;
            }

            void createSignedBookExportArtifactDownload(artifact)
              .then((signedArtifact) => {
                window.open(signedArtifact.downloadUrl, "_blank", "noopener,noreferrer");
              })
              .catch(() => {
                showNotice({
                  kind: "error",
                  message: "bookAssembly.errors.downloadExpired",
                });
              });
          }}
          onGenerate={() => {
            if (!window.confirm(t("bookAssembly.generate.savedContentConfirm"))) {
              return;
            }

            void generateBook();
          }}
          onPrepareFreshExport={() => {
            prepareFreshExport();
          }}
          onRetry={(jobId) => {
            void retryJob(jobId);
          }}
          run={draft.run}
          summary={summary}
        />
      ) : null}

      <div className="flex flex-wrap justify-between gap-3">
        <Button
          disabled={!previousStep}
          onClick={() => {
            if (previousStep) {
              setStep(previousStep);
            }
          }}
          type="button"
          variant="outline"
        >
          {t("bookAssembly.actions.previous")}
        </Button>
        <Button
          disabled={!nextStep}
          onClick={() => {
            if (nextStep) {
              setStep(nextStep);
            }
          }}
          type="button"
        >
          {nextStep ? t("bookAssembly.actions.next") : t("bookAssembly.generate.generate")}
        </Button>
      </div>
    </div>
  );
}
